import type { GroupAnalysisSpanData } from '@/generated/types/navigator/types.gen';
import { Insight } from '@/views/map/Insight/Insight';
import {
  getWildfireRiskText,
  PriorityRatingType,
  Rating,
} from '@/features/types';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { Section } from '@/views/list/level3/Section';
import { HelpPopover } from '@/components/HelpPopover/HelpPopover';
import { analysisView } from '@/app/analysis';
import { PriorityRating } from '@/views/list/field/PriorityRating';
import { EncroachmentRiskRating } from '@/views/list/field/EncroachmentRiskRating';
import { FuelHazardRating } from '@/views/list/field/FuelHazardRating';
import { HELP_ARTICLES } from '@/helpers/helpArticles';
import { useUserClientContext } from '@/app/clientContext';
import { useDatasets } from '@/hooks/useDatasets';
import { useHasWildfirePriority } from '@/hooks/useHasWildfirePriority';
import {
  OUTSIDE_ROW,
  useMinDistToLineFormatter,
} from '@/views/list/Level3Item';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { isPge } from '@/features/utils';
import { useCallback, useMemo, useState } from 'react';
import { useWildfireMode, useHasFuelsData } from '@/hooks/useWildfireMode';
import { useViewedOnceAnalytics } from '@/hooks/useViewedOnceAnalytics';
import { GoogleEvents } from '@/features/googleEvents';
import { FuelsSection } from '@/views/map/popover/FuelsSection';
import { WorkTypeAnalysisChart } from '@/views/map/popover/WorkTypeAnalysisChart';
import {
  formatWorkTypeRows,
  type WorkTypeRow,
  type WorkTypeView,
} from '@/features/workTypeAnalysis';

export const RiskTab = (a: GroupAnalysisSpanData) => {
  const { context } = useUserClientContext();
  const { datasets } = useDatasets();
  const { getClientTerm, formatMessage, formatNumber } = useClientTerminology();
  const wildfireMode = useWildfireMode();
  const hasFuelsData = useHasFuelsData();
  const hasWildfirePriority = useHasWildfirePriority();
  const fuelsRef = useViewedOnceAnalytics<HTMLDivElement>(
    GoogleEvents.spanFuelsViewed,
    a.level3
  );
  const minDistToLine = useMinDistToLineFormatter(
    a.minDistSpanInFt ?? undefined,
    a.bufferInFt ?? undefined,
    context.analysis_config.metric
  );
  const overhang = a.hasOverhang ? 'Yes' : 'No';
  const [workTypeView, setWorkTypeView] = useState<WorkTypeView>('area');
  const formatWorkTypeAmount = useCallback(
    ({ amount, unit }: WorkTypeRow) =>
      unit === 'sqft' || unit === 'sqm'
        ? formatMessage(
            { id: `level3.workType.unit.${unit}` },
            { value: formatNumber(amount) }
          )
        : formatNumber(amount, {
            style: 'unit',
            unit: unit === 'ft' ? 'foot' : 'meter',
            unitDisplay: 'short',
          }),
    [formatMessage, formatNumber]
  );

  const wildfireRating = a.wildfireRating ?? undefined;
  const treeCoverPct = a.treeCoverPct ?? undefined;

  const enablementOptions = useMemo(() => {
    const hasPriority = !isPge(context);

    const hasKnownReliabilityPriority =
      a.priorityRating >= PriorityRatingType.P1 &&
      a.priorityRating <= PriorityRatingType.P5;

    return {
      hasPriority,
      hasWildfirePriority,
      hasKnownReliabilityPriority,
      wildfireMode,
      showClearSpanAnalysis: false,
      hasCriticalTreesCount: Number.isFinite(a.criticalTreesCount),
      hasOverhang: true,
      hasMinDistSpan: a.minDistSpanInFt != null,
      hasClosestTree: a.closestTree != null,
      hasStrikeTreesCount: Number.isFinite(a.strikeTreesCount),
      hasHazardTreesCount: Number.isFinite(a.hazardTreesCount),
      hasDecliningTreesCount: Number.isFinite(a.decliningTreesCount),
      hasPriorityData: !!(
        hasPriority ||
        hasWildfirePriority ||
        Number.isFinite(a.wildfireRating)
      ),
      datasets,
    };
  }, [context, a, wildfireMode, hasWildfirePriority, datasets]);

  return (
    <>
      <Section name="">
        {enablementOptions.hasPriority && (
          <Insight
            name={formatMessage({
              id: analysisView[GroupAnalysisType.PRIORITY].getLabel(
                context,
                enablementOptions
              ),
            })}
            tooltip={
              <HelpPopover
                link={analysisView[GroupAnalysisType.PRIORITY].helpLink}
                linkText={
                  analysisView[GroupAnalysisType.PRIORITY].helpShortText
                }
              />
            }
          >
            <PriorityRating priorityRating={a.priorityRating} />
          </Insight>
        )}
        {enablementOptions.hasWildfirePriority && (
          <Insight
            name={formatMessage({
              id: analysisView[GroupAnalysisType.WILDFIRE_PRIORITY].getLabel(
                context,
                enablementOptions
              ),
            })}
            tooltip={
              <HelpPopover
                link={
                  analysisView[GroupAnalysisType.WILDFIRE_PRIORITY].helpLink
                }
                linkText={
                  analysisView[GroupAnalysisType.WILDFIRE_PRIORITY]
                    .helpShortText
                }
              />
            }
          >
            <PriorityRating priorityRating={a.wildfirePriorityRating} />
          </Insight>
        )}
        {wildfireRating !== undefined && (
          <Insight
            name="Wildfire risk zone"
            data={getWildfireRiskText(wildfireRating)}
          />
        )}
      </Section>
      <Section
        name="Encroachment"
        showDivider={enablementOptions.hasPriorityData}
        headerRight={
          <EncroachmentRiskRating
            rating={a.encRating ?? Rating.Unknown}
            score={a.encScore ?? -1}
          />
        }
      >
        {enablementOptions.hasCriticalTreesCount && (
          <Insight
            name={getClientTerm('level3.criticalTreesCount')}
            data={`${a.criticalTreesCount}`}
          />
        )}
        {treeCoverPct !== undefined && (
          <Insight name="Tree cover" data={`${treeCoverPct.toFixed(0)}%`} />
        )}
        {enablementOptions.hasOverhang && (
          <Insight name="Overhang" data={overhang} />
        )}
        {/* Closest tree takes precedence over min. dist to conductor */}
        {enablementOptions.hasMinDistSpan &&
          !enablementOptions.hasClosestTree && (
            <Insight name="Min. distance to conductor" data={minDistToLine} />
          )}
        {enablementOptions.hasClosestTree && (
          <Insight
            name="Closest tree"
            data={
              a.closestTree === OUTSIDE_ROW
                ? OUTSIDE_ROW
                : `${a.closestTree}${a.metric}`
            }
          />
        )}
      </Section>
      <Section name="Fall-In" showDivider={true}>
        {enablementOptions.hasStrikeTreesCount && (
          <Insight
            name={getClientTerm('level3.strikeTreesCount')}
            data={`${a.strikeTreesCount}`}
          />
        )}
        {enablementOptions.hasHazardTreesCount && (
          <Insight
            name={getClientTerm('level3.hazardTreesCount')}
            data={`${a.hazardTreesCount}`}
          />
        )}
        {enablementOptions.hasDecliningTreesCount && (
          <Insight
            name={getClientTerm('level3.decliningTreesCount')}
            data={`${a.decliningTreesCount}`}
          />
        )}
      </Section>
      {a.workTypeAnalysis != null && (
        <Section
          name={formatMessage({ id: 'level3.workType.title' })}
          showDivider={true}
        >
          <WorkTypeAnalysisChart
            rows={formatWorkTypeRows(
              a.workTypeAnalysis,
              workTypeView,
              context.analysis_config.metric ?? 'ft'
            )}
            view={workTypeView}
            onViewChange={setWorkTypeView}
            formatAmount={formatWorkTypeAmount}
          />
        </Section>
      )}
      {hasFuelsData &&
        a.fuelCategories != null &&
        a.fuelHazardRating != null &&
        enablementOptions.hasKnownReliabilityPriority && (
          <div ref={fuelsRef}>
            <Section
              name={formatMessage({ id: 'level3.fuelHazard.title' })}
              showDivider={true}
              tooltip={
                <HelpPopover
                  text={formatMessage({ id: 'level3.fuelHazard.tooltip' })}
                  link={HELP_ARTICLES.wildfireIntelligenceFuelHazard}
                  linkText={formatMessage({ id: 'list.learnMore' })}
                />
              }
              headerRight={
                <FuelHazardRating
                  rating={a.fuelHazardRating}
                  score={a.fuelHazardScore}
                />
              }
            >
              <FuelsSection fuelCategories={a.fuelCategories} />
            </Section>
          </div>
        )}
    </>
  );
};
